// The analysis worker's message contract (P2.4, revised at P2.5, staged at P3.1) — Architecture §5, §11.
//
// **The worker is handed the track memory, not the decoded channels, and that changed at P2.5 when
// the caller arrived.** P2.4 wrote this to carry `Float32Array[]`, which forced an unpleasant
// choice: transfer them and *detach* the arrays the deck keeps for rebuilding the node, or clone
// them and pay a ~138 MB structured-clone copy on the load path FR-10 says must never block.
//
// Neither is necessary. `Deck.loadFile` has already copied the PCM into a `SharedArrayBuffer` for
// the audio thread, and shared memory is shared with every thread, not just that one. So the worker
// receives the same buffer and builds its own views: no copy, no transfer, nothing detached, and
// one source of samples rather than two that could drift apart.
//
// The lesson generalises, and P3.1's stages are where it pays off: **a `SharedArrayBuffer` in a
// message is free, and a typed array in a message is either a copy or an amputation.** Five stages
// reading the same track means five chances to get that wrong; they all read the one buffer.

import type { TrackMemorySpec } from './track-memory';
import type { WaveformMipmapSpec } from './waveform-mipmap';

/**
 * Architecture §5's pipeline, in order, and **the order is not arbitrary**.
 *
 * Mipmaps first because FR-11 budgets them at ≤2 s and the waveform is what makes a loaded track
 * feel loaded. Loudness second because it is cheap and the UI wants it early (FR-10's normalisation
 * gain changes what the user hears the moment they press play). Then the expensive chain: onsets
 * feed tempo, and tempo is the input to the beatgrid that Sync aligns to. Key last because nothing
 * waits on it.
 *
 * Declared as data rather than as a sequence of calls so the pipeline can be *iterated* — which is
 * what lets a test assert that every stage is represented in a record, and what stops a stage being
 * silently dropped by an edit to a function body.
 */
export const ANALYSIS_STAGES = ['mipmaps', 'loudness', 'onsets', 'tempo', 'key'] as const;

export type AnalysisStage = (typeof ANALYSIS_STAGES)[number];

/**
 * Why the normalisation gain is the number it is (P3.2).
 *
 * Carried rather than derived, because "quiet track, turned up as far as it safely goes" and "quiet
 * track, turned up to target" look identical from the gain alone and mean different things to
 * whoever is deciding whether to trust it.
 *
 *   * `normalised` — the gain hits `LOUDNESS_TARGET_LUFS` exactly. The ordinary case.
 *   * `peak-limited` — a boost was pulled back so the loudest sample stays under the ceiling.
 *   * `boost-limited` — a boost hit `LOUDNESS_MAX_GAIN_DB` first. The track is very quiet.
 *   * `silent` — no block rose above the -70 LUFS absolute gate. Gain is unity.
 *   * `too-short` — under 400 ms, so BS.1770 has no whole measurement window. Gain is unity.
 */
export type LoudnessGainReason =
  'normalised' | 'peak-limited' | 'boost-limited' | 'silent' | 'too-short';

/**
 * FR-10's measurement and the gain derived from it (P3.2).
 *
 * Lives here rather than beside the arithmetic in `audio/analysis/loudness.ts` because it crosses
 * the worker boundary, which is what this file is for.
 */
export interface LoudnessAnalysis {
  /**
   * Integrated loudness, LUFS. **Null when the track has none** — silence, or a file too short for
   * one 400 ms window — rather than -Infinity or a sentinel number that arithmetic would swallow.
   */
  readonly lufs: number | null;
  /** Loudest absolute sample in the track, linear. Not a true-peak estimate; see `loudness.ts`. */
  readonly peak: number;
  /** The normalisation gain, linear. **Never 0, never NaN**, and 1 means "leave it alone". */
  readonly gain: number;
  readonly reason: LoudnessGainReason;
  /** 400 ms windows measured, and how many survived both gates. Present so "the gate did nothing"
   * is a readable fact rather than an assumption — this repo has shipped that bug six times. */
  readonly blockCount: number;
  readonly gatedBlockCount: number;
}

/**
 * What the onset stage reports — **the shape of the novelty curve, not the curve** (P3.3).
 *
 * The curve itself is tens of thousands of floats and has exactly one consumer, the tempo stage,
 * which runs in the same worker and takes it directly. Posting it would be a structured clone of
 * ~130 kB per track so the main thread could hold something nothing on the main thread reads. If
 * P3.12's grid tools or a beat-marked ribbon ever want it, that is the moment to carry it — noted in
 * `BACKLOG-NOTES.md` rather than guessed at now.
 *
 * `strongFrameCount` is the one field here that is not merely descriptive. An all-zero curve — from
 * silence, a decimation bug, or an FFT that returned its input — still yields a *confident-looking*
 * tempo, because autocorrelating a constant produces a peak wherever the tie-break lands. This is
 * what makes that visible instead of silent.
 */
export interface OnsetAnalysis {
  /** Novelty frames per second. Differs between 44.1 and 48 kHz material, so it travels with the
   * numbers rather than being assumed by whoever reads them. */
  readonly frameRateHz: number;
  readonly frameCount: number;
  readonly meanNovelty: number;
  readonly peakNovelty: number;
  /** Frames more than three times the mean — a crude count of transients. Zero on a drone, and zero
   * on a curve that was never computed. */
  readonly strongFrameCount: number;
}

/**
 * One move up or down the octave ladder, with the evidence that caused it (P3.3).
 *
 * P3.3's rule is that the half/double decision must be "a named, testable step, not a heuristic
 * buried in a scoring loop". This is what makes that literally true: a wrong tempo can be read
 * backwards to the rule that produced it and the measurement that justified it, and a test can
 * assert on the *reasoning* rather than only on the answer.
 *
 *   * `fold-up` / `fold-down` — the raw estimate was outside the reporting range. Carries no ratio,
 *     because it is not a judgement about the music.
 *   * `subdivide` — the midpoints were indistinguishable from the beats, so they were beats.
 *   * `merge` — alternate pulses were systematically weaker, so the slower period was the beat.
 */
export interface OctaveStep {
  readonly rule: 'fold-up' | 'fold-down' | 'subdivide' | 'merge';
  readonly fromBpm: number;
  readonly toBpm: number;
  /** The midpoint ratio behind the move. Null for the range folds, which measure nothing. */
  readonly ratio: number | null;
}

/**
 * A constant-tempo beatgrid (P3.5) — what Sync actually aligns to.
 *
 * Separate from the BPM beside it because they answer different questions and fail differently. A
 * BPM identifies a tempo; a grid says *when the beats are*, and AC-2 budgets its drift at 25 ms per
 * minute — about 0.05 BPM at 128, some twenty times tighter than the tolerance P3.4's corpus scores
 * a BPM against. Full reasoning in `audio/analysis/grid.ts`.
 */
export interface BeatGrid {
  /** When the first beat happens, in seconds from the start of the track. */
  readonly anchorSeconds: number;
  /** Seconds between beats. Carried rather than derived from the BPM, so a consumer cannot
   * reintroduce a period error by rounding. */
  readonly beatSeconds: number;
  /** How well one constant grid describes the whole track, 0…1. Low means the tempo moves — and
   * v1's answer to that is a low confidence, never a fitted curve. */
  readonly fit: number;
}

/**
 * Why the tempo stage produced what it did.
 *
 *   * `detected` — a tempo, with a confidence that may still be low. Low confidence is an answer.
 *   * `too-short` — under 32 beats at the slowest tempo in range; a number from less is noise.
 *   * `no-pulse` — the novelty curve is flat. Silence, or nothing rhythmic in it at all.
 */
export type TempoReason = 'detected' | 'too-short' | 'no-pulse';

/**
 * FR-09's BPM, and everything needed to decide whether to believe it (P3.3).
 *
 * **`confidence` is a minimum of two independent measurements, not a blend**, and the distinction is
 * the point. `pulseClarity` asks whether the track pulses at all; `octaveMargin` asks whether the
 * half/double choice was close. A weighted average lets a rock-solid pulse hide a coin-flip octave —
 * which is the failure a DJ actually hears, because being out by a factor of two is not being
 * slightly wrong. FR-13's manual tools exist for the ~5% this gets wrong, and a UI cannot prompt for
 * a correction it cannot tell apart from a certainty.
 */
export interface TempoAnalysis {
  /** Beats per minute, or null when `reason` says there is none. Never NaN. */
  readonly bpm: number | null;
  /** 0…1. `min(pulseClarity, octaveMargin)`. */
  readonly confidence: number;
  readonly reason: TempoReason;
  /** What the autocorrelation peak said before any octave move. Kept because "the detector found
   * 174 and the octave rule halved it" and "the detector found 87" are different facts. */
  readonly rawBpm: number | null;
  readonly octaveSteps: readonly OctaveStep[];
  /** How far the autocorrelation peak stood above the rest of the search range, 0…1. */
  readonly pulseClarity: number;
  /** How far the deciding midpoint ratio sat from its threshold, 0…1. Zero was a coin flip. */
  readonly octaveMargin: number;
  /**
   * The grid Sync aligns to (P3.5). Null when there is no tempo to build one from.
   *
   * Its `fit` is the third input to `confidence`, alongside `pulseClarity` and `octaveMargin` — a
   * track can pulse unmistakably, at an unambiguous octave, and still not be describable by one
   * constant grid, and that is a way of being untrustworthy the other two cannot see.
   */
  readonly grid: BeatGrid | null;
}

export type KeyMode = 'major' | 'minor';

/**
 * FR-09's key readout (P3.5) — twelve pitch classes and a mode, never a piece of notation.
 *
 * `tonic` is a pitch class, 0 = C. **Deliberately a number rather than "F#" or "8A"**: the first is
 * a spelling question (F# and Gb are the same key and different words) and the second is Camelot,
 * which P3.5's rule keeps next to the UI that renders it. A DSP stage that emitted "8A" would have
 * baked a display convention into the analysis record and into FR-12's cache with it.
 *
 * Null when there is nothing pitched to measure. Silence has no key, and neither does a drum loop —
 * and "C major" is the answer a template match gives to an empty chroma if nobody stops it.
 */
export interface KeyAnalysis {
  readonly tonic: number | null;
  readonly mode: KeyMode | null;
  /**
   * How well the winning profile fits, 0…1 — **not how far ahead of the runner-up**.
   *
   * The runner-up is nearly always the relative major or minor, which shares a Camelot number and
   * which a DJ mixes between freely. Scoring the margin would report near-zero confidence on every
   * correctly-detected track. See `audio/analysis/key.ts`.
   */
  readonly confidence: number;
  /** The raw correlation, which can be negative. Kept because a *negative* best match means the
   * chroma looks like no key at all, and that is different from a weak one. */
  readonly correlation: number;
}

/**
 * What each stage's `done` outcome carries.
 *
 * **`never` means the stage cannot yet produce a value**, and that is load-bearing rather than
 * decorative: `StageOutcome<never>` has an uninhabitable `done` variant, so an unimplemented stage
 * cannot claim a result even by accident. Swapping a `never` for a real type is what each of
 * P3.3–P3.5 does, and TypeScript forces every reader to handle it at that moment.
 *
 * P3.2 made the first such swap and P3.3 made the next two. Neither was free and neither was meant
 * to be: `applyStageComplete` below, the host's switch, and `StageValue` in the pipeline all had to
 * grow a case each time — which is the mechanism working, not friction to be routed around.
 */
export interface StageValues {
  /** The mipmap levels live in the shared buffer, so the stage's own value is nothing. */
  readonly mipmaps: null;
  /** P3.2 — FR-10's integrated loudness and the gain it implies. */
  readonly loudness: LoudnessAnalysis;
  /** P3.3 — the shape of the spectral-flux novelty curve. The curve stays in the worker. */
  readonly onsets: OnsetAnalysis;
  /** P3.3 — tempo with the half/double decision. P3.5 adds the constant-tempo grid fit. */
  readonly tempo: TempoAnalysis;
  /** P3.5 — chroma → template-matched key. Camelot lives next to the UI. */
  readonly key: KeyAnalysis;
}

/**
 * Anything a stage can carry, as one type.
 *
 * Derived from `StageValues` rather than written out, so it grows by itself as each prompt swaps a
 * `never` for a real result. Today that resolves to `null | LoudnessAnalysis` — the `never`s
 * contribute nothing to a union, which is precisely the property that makes them worth having.
 */
export type StageValue = StageValues[AnalysisStage];

/**
 * What became of one stage.
 *
 * **The four states exist because P3.1's rule demands three of them be distinguishable**: "a stage
 * that returns `null` must be distinguishable from a stage that has not run". Collapsing those into
 * a nullable value is the mistake — a UI cannot tell "no key detected in this track" from "the key
 * stage has not got there yet" from "the key stage is not built", and those want three different
 * things on screen.
 *
 *   * `pending` — the pipeline has not reached it. The honest state at the start of every load.
 *   * `skipped` — it ran and deliberately did nothing, because the stage is not implemented yet.
 *     Carries the reason, so the UI shows "—" with an explanation rather than an error.
 *   * `done` — it ran and produced `value`, in `elapsedMillis`.
 *   * `failed` — it threw. **Earlier stages keep their outcomes**; see `AnalysisStages`.
 */
export type StageOutcome<T> =
  | { readonly status: 'pending' }
  | { readonly status: 'skipped'; readonly reason: string }
  | { readonly status: 'done'; readonly value: T; readonly elapsedMillis: number }
  | { readonly status: 'failed'; readonly reason: string };

/**
 * Every stage's outcome, whether or not it ran.
 *
 * Derived from `StageValues` rather than restating it, so the two cannot drift: the per-stage
 * documentation of what a value *is* lives there, and this says only what shape wraps it.
 *
 * That is the same trick P0.5 used on `WorkletMessage` and P0.11 on `TelemetryEvent`, both of which
 * collected: a type designed to break on the change is worth more than a comment describing it.
 */
export type AnalysisStages = {
  readonly [S in AnalysisStage]: StageOutcome<StageValues[S]>;
};

/** Every stage `pending`. The state a record starts in, so "not run" is never `undefined`. */
export function pendingStages(): AnalysisStages {
  return {
    mipmaps: { status: 'pending' },
    loudness: { status: 'pending' },
    onsets: { status: 'pending' },
    tempo: { status: 'pending' },
    key: { status: 'pending' },
  };
}

/**
 * Folds one `stage-complete` into the record (P3.2).
 *
 * **This is where a stage's claim to have produced a value is checked**, and it is a switch with a
 * case per stage rather than a computed key for a reason worth stating. `{ ...stages, [stage]: … }`
 * type-checks against an index signature and quietly accepts anything; a stage that is declared
 * `StageOutcome<never>` would be able to arrive carrying a value and nothing would notice, which
 * defeats the whole point of typing it `never`. Written out, the compiler forces P3.3 to come here
 * and say what an onset result is before one can exist.
 *
 * The runtime checks are not ceremony either. The worker ships in its own bundle and caches
 * separately from the app (P0.8's finding, ADR-L2), so "a stage sent a value the app's type says is
 * impossible" is a version skew, not a theoretical concern — and it becomes a `failed` outcome
 * naming the stage instead of an `undefined` propagating into a gain.
 */
export function applyStageComplete(
  stages: AnalysisStages,
  stage: AnalysisStage,
  value: StageValue,
  elapsedMillis: number,
): AnalysisStages {
  const wrongShape = (): AnalysisStages => ({
    ...stages,
    [stage]: {
      status: 'failed',
      reason: `The ${stage} stage reported a result this build does not understand. The analysis worker and the app may be different versions; a hard reload usually fixes it.`,
    },
  });

  switch (stage) {
    case 'mipmaps':
      // The levels are the shared buffer the host allocated and has been reading throughout, so
      // there is nothing to carry and nothing to check beyond "it did not invent one".
      return value === null
        ? { ...stages, mipmaps: { status: 'done', value: null, elapsedMillis } }
        : wrongShape();

    case 'loudness':
      // `gain` is checked, not assumed. It is the one field that reaches the audio thread, and
      // `loudness.ts` promises it is never 0 and never NaN; a promise worth making is worth
      // enforcing at the boundary where it would otherwise become silence nobody can explain.
      return value !== null &&
        'gain' in value &&
        Number.isFinite(value.gain) &&
        value.gain > 0
        ? { ...stages, loudness: { status: 'done', value, elapsedMillis } }
        : wrongShape();

    case 'onsets':
      // A curve with no frames is not an onset analysis, it is a stage that ran on nothing. Checked
      // here for the same reason the gain is: the tempo stage divides by this frame rate.
      return value !== null && 'frameRateHz' in value && value.frameRateHz > 0
        ? { ...stages, onsets: { status: 'done', value, elapsedMillis } }
        : wrongShape();

    case 'tempo':
      // `bpm` is allowed to be null — `reason` says why — but a *number* that is not a number is
      // the failure this catches, and it would otherwise reach a readout and a beatgrid.
      return value !== null &&
        'octaveSteps' in value &&
        (value.bpm === null || value.bpm > 0)
        ? { ...stages, tempo: { status: 'done', value, elapsedMillis } }
        : wrongShape();

    case 'key':
      // The last of the five. `StageValues.key` was `never` from P3.1 until P3.5, and every prompt
      // that filled a stage had to come here and say what a valid result looks like — which is what
      // the `never` was for. A tonic outside 0…11 would index a note-name table out of bounds.
      return value !== null &&
        'tonic' in value &&
        (value.tonic === null || (value.tonic >= 0 && value.tonic < 12))
        ? { ...stages, key: { status: 'done', value, elapsedMillis } }
        : wrongShape();
  }
}

export interface AnalyseTrackMessage extends WaveformMipmapSpec {
  readonly type: 'analyse-track';
  /** The deck's track memory, shared rather than transferred. Read-only from the worker's side. */
  readonly trackMemory: SharedArrayBuffer;
  /** How to read it. The worker derives the same layout the deck did, from the same function. */
  readonly track: TrackMemorySpec;
  /** Host-allocated, sized by `computeWaveformMipmapLayout(...).totalBytes`. */
  readonly mipmapBuffer: SharedArrayBuffer;
}

/**
 * One variant today, and deliberately no union bar.
 *
 * P0.5 wrote the worklet's handler the same way and P0.12 collected on it: with one variant a
 * dispatch is a branch the linter can prove is dead, and the union is still the enforcement — a
 * second variant makes TypeScript reject the destructure and forces the switch at the moment it
 * starts earning its place.
 */
export type AnalysisWorkerMessage = AnalyseTrackMessage;

/**
 * A stage finished, one message each, **as it happens rather than at the end**.
 *
 * This is the whole point of P3.1. FR-11 wants progressive availability — the waveform visible at
 * ≤2 s while tempo and key take as long as they take — and a single resolve-at-the-end promise
 * cannot express that. A load is now a stream of these followed by one `analysis-complete`.
 *
 * **`value` arrived at P3.2**, which is when a stage first had something to send. P3.1 left it out
 * deliberately: the only stage that produced anything wrote it into shared memory (the mipmap
 * levels), and inventing a payload shape three prompts before any DSP argued for one would have been
 * guessing.
 *
 * `stage` and `value` are typed independently rather than as a correlated pair, and that is a
 * decision rather than an oversight. TypeScript cannot carry the correlation through a heterogeneous
 * stage map without generics that infect the pipeline runner, the worker and the host — so the
 * pairing is checked once, at the only place it matters, by `applyStageComplete`. One runtime check
 * in exchange for a pipeline that stays readable.
 */
export interface StageCompleteResponse {
  readonly type: 'stage-complete';
  readonly stage: AnalysisStage;
  readonly elapsedMillis: number;
  /** Null for every stage whose output lives somewhere else, which today is all but loudness. */
  readonly value: StageValue;
}

/** A stage that is not built yet ran and said so. Not an error, and must not look like one. */
export interface StageSkippedResponse {
  readonly type: 'stage-skipped';
  readonly stage: AnalysisStage;
  readonly reason: string;
}

/** Every stage has been attempted. Carries the wall time for the whole pipeline. */
export interface AnalysisCompleteResponse {
  readonly type: 'analysis-complete';
  readonly totalMillis: number;
}

/**
 * Something threw, and **which stage** it threw in.
 *
 * The stage is new at P3.1 and it is the difference between a usable failure and a shrug: with five
 * stages, "analysis failed" tells a user nothing and tells a developer less. It also lets the host
 * keep every earlier stage's result — a track whose key detection throws still has a waveform, and
 * throwing that away would be losing work that already succeeded.
 *
 * A string, not the Error: FR-42's posture is that a failure explains itself rather than breaking
 * the deck, and structured-cloning an Error across a worker boundary loses the stack anyway. The
 * message is scrubbed of nothing here because nothing here has seen a filename — the worker is
 * handed buffers and integers, never a `File`. **That is worth keeping true as this file grows**,
 * and it grows a lot in M3.
 */
export interface AnalysisFailedResponse {
  readonly type: 'analysis-failed';
  readonly stage: AnalysisStage;
  readonly reason: string;
}

export type AnalysisWorkerResponse =
  | StageCompleteResponse
  | StageSkippedResponse
  | AnalysisCompleteResponse
  | AnalysisFailedResponse;
